import {jwtDecode} from 'jwt-decode'

import {logger} from '#/logger'

/**
 * Simple check if a JWT token has expired. Does *not* validate the token or check for revocation status,
 * just checks the expiration time.
 *
 * @param token The JWT token to check.
 * @returns `true` if the token has expired, `false` otherwise.
 */
export function isJwtExpired(token: string) {
  try {
    const payload = jwtDecode(token)

    if (!payload.exp) return true
    const now = Math.floor(Date.now() / 1000)
    return now >= payload.exp
  } catch {
    logger.error(`session: could not decode jwt`)
    return true // invalid token or parse error
  }
}

/**
 * Returns `iat` and `exp` fields from a JWT, or `undefined` if the token
 * cannot be decoded or is missing those claims.
 */
export function getJwtExpiry(
  token: string,
): {iat: number; exp: number} | undefined {
  try {
    const payload = jwtDecode(token)
    if (payload.exp == null || payload.iat == null) return undefined
    return {iat: payload.iat, exp: payload.exp}
  } catch {
    return undefined
  }
}

/**
 * Returns milliseconds until 2/3 of the token lifetime has elapsed (the
 * proactive-refresh threshold). Returns `null` when the timing cannot be
 * determined (missing claims), and `0` when the threshold has already passed.
 */
export function msUntilTokenNeedsRefresh(token: string): number | null {
  const expiry = getJwtExpiry(token)
  if (!expiry) return null
  const twoThirdsPointMs =
    (expiry.iat + Math.floor(((expiry.exp - expiry.iat) * 2) / 3)) * 1000
  return Math.max(0, twoThirdsPointMs - Date.now())
}

export function isAppPassword(token: string) {
  const payload = jwtDecode(token)
  // @ts-ignore
  return payload.scope === 'com.atproto.appPass'
}
